import { Component, PureComponent } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import parse from "csv-parse/lib/es5/sync";
import chunk from "lodash/chunk";
import { List } from "react-window";
import cx from "classnames";

import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import FileUploadIcon from "@mui/icons-material/FileUpload";

import { Notice } from "shell/components/legacy/Notice";

import { Columns } from "./Columns";
import { CsvSettings } from "./CsvSettings";

import { request } from "utility/request";
import { notify } from "shell/store/notifications";
import { fetchFields } from "shell/store/fields";

import styles from "./CSVImport.less";
import { Box } from "@mui/material";
class CSVImportBody extends Component {
  state = {
    modelZUID: this.props.modelZUID,
    fields: this.props.fields,
    warn: "",
    complete: false,
    inFlight: false,
    success: [],
    failure: [],
    cols: [],
    records: [],
    successes: 0,
    height: document.documentElement.clientHeight - 335,
    webMaps: {
      metaDescription: "",
      metaKeywords: null,
      metaLinkText: "",
      metaTitle: "",
      parentZUID: "0", // this should validate that the mapped col contains ZUIDs
      pathPart: "",
      sitemapPriority: -1,
      canonicalTagMode: 0,
    },
    fieldMaps: {},
  };

  chunkSize = 30;

  componentDidMount() {
    // if there are no fields, we need to fetch them here
    if (!this.props.fields || !this.props.fields.length) {
      this.props.dispatch(fetchFields(this.props.modelZUID));
    }
  }

  componentDidUpdate() {
    // update fields if we dont have any, or if the content model has changed
    if (
      this.state.modelZUID !== this.props.modelZUID ||
      (!this.state.fields.length && this.props.fields.length)
    ) {
      this.setState({ fields: this.props.fields });
    }
  }

  handleFile = (evt) => {
    evt.preventDefault();

    // Do nothing if no file was selected because onChange gets triggered even if
    // cancel button was clicked
    if (!evt.target.files.length) {
      return;
    }

    const csvToParse = evt.target.files[0];

    // make sure we have a csv and throw an error if we dont
    if (csvToParse.name.slice(-3).toLowerCase() !== "csv") {
      return this.setState({
        warn: this.props.t("content.csvImportNotCsvFile"),
      });
    }

    this.setState({
      complete: false,
      warn: "",
    });

    // takes the file object and reads it
    // as a text string into local state
    const reader = new FileReader();
    reader.onload = (evt) => {
      this.setState({ csv: evt.target.result }, () => {
        // after the text string is set to state
        // we can run our parse to get the records
        this.parseCSV(this.state.csv);
      });
    };
    reader.readAsText(csvToParse);
  };

  parseCSV = (csv) => {
    try {
      const records = parse(csv, {
        skip_empty_lines: true,
        skip_lines_with_empty_values: true,
      });

      // Handle empty csv imports
      if (!records.length) {
        return this.setState({
          warn: this.props.t("content.csvImportEmptyFile"),
          cols: [],
          records: [],
        });
      }

      // build an array of object to reference data across columns
      const columnsWithValues = records.slice(1).reduce((acc, item, i) => {
        const rec = item.reduce((ac, it, i) => {
          ac[records[0][i]] = it;
          return ac;
        }, {});
        acc.push(rec);
        return acc;
      }, []);

      this.setState({
        cols: records[0],
        records: columnsWithValues,
      });
    } catch (error) {
      this.setState({
        warn: error.message.includes("Invalid Record Length")
          ? this.props.t("content.csvImportColumnMismatch")
          : error.message,
        cols: [],
        records: [],
      });
    }
  };

  handleFieldToCSVMap = (fieldName, csvCol) => {
    if (fieldName === "none") {
      // filter out the field association if it exists in the fieldMap
      if (this.state.fieldMaps[csvCol]) {
        let removedFieldMap = { ...this.state.fieldMaps };
        delete removedFieldMap[csvCol];
        return this.setState({ fieldMaps: removedFieldMap }, () => {
          this.mapFieldsToCols();
        });
      } else {
        // selecting none should not map anything
        return null;
      }
    } else {
      // map the association
      this.setState(
        {
          fieldMaps: { ...this.state.fieldMaps, [csvCol]: fieldName },
        },
        () => {
          this.mapFieldsToCols();
        }
      );
    }
  };

  mapFieldsToCols = () => {
    // Using a set to remove duplication
    let warnFields = new Set();

    // for each record create an item with the corresponding
    // data matched to the field name from the model
    const mappedItems = this.state.records.map((rec) => {
      let data = Object.keys(rec).reduce((acc, key, i) => {
        if (this.state.fieldMaps[key]) {
          if (acc[this.state.fieldMaps[key]]) {
            warnFields.add(this.state.fieldMaps[key]);
          }
          acc[this.state.fieldMaps[key]] = rec[key];
        }
        return acc;
      }, {});
      return { data };
    });

    // warn the user of duplicate column use
    let warn = "";
    if (warnFields.size) {
      warn = this.props.t("content.csvImportDuplicateColumns", {
        columns: Array.from(warnFields).join(" & "),
      });
    }
    this.setState({ mappedItems, warn });
  };

  handleWebToCSVMap = (csvCol, webKey) => {
    this.setState({
      complete: false,
      webMaps: {
        ...this.state.webMaps,
        [webKey]: csvCol === "none" ? "" : csvCol,
      },
    });
  };

  addFailure = (index) => {
    // add the failed index to the array of failed items
    this.setState({ failure: [...this.state.failure, index] });
  };

  handleCreateItems = () => {
    this.setState({ failure: [] });
    if (!this.props.model?.type === "dataset" && !this.state.webMaps.pathPart) {
      return this.props.dispatch(
        notify({
          message: this.props.t("content.csvImportSelectPathPart"),
          kind: "warn",
        })
      );
    }
    if (this.state.warn) {
      return this.props.dispatch(
        notify({
          message: this.props.t("content.csvImportResolveDuplicates"),
          kind: "warn",
        })
      );
    }

    const mappedMetaItems = this.state.mappedItems.map((item, i) => {
      let pathPart = null;
      if (this.state.webMaps.pathPart) {
        pathPart = this.state.records[i][this.state.webMaps.pathPart]
          .trim()
          .toLowerCase()
          .replace(/\&/g, "and")
          .replace(/[^a-zA-Z0-9]/g, "-");
      }

      return {
        ...item,
        web: {
          canonicalQueryParamWhitelist:
            this.state.webMaps.canonicalQueryParamWhitelist || null,
          canonicalTagCustomValue:
            this.state.webMaps.canonicalTagCustomValue || null,
          canonicalTagMode: this.state.webMaps.canonicalTagMode,
          metaDescription: this.state.records[i][
            this.state.webMaps.metaDescription
          ]
            ? this.state.records[i][this.state.webMaps.metaDescription].substr(
                0,
                160
              )
            : "",
          metaKeywords: this.state.records[i][this.state.webMaps.metaKeywords],
          metaLinkText: this.state.records[i][this.state.webMaps.metaLinkText]
            ? this.state.records[i][this.state.webMaps.metaLinkText].substr(
                0,
                150
              )
            : "",
          metaTitle: this.state.records[i][this.state.webMaps.metaTitle]
            ? this.state.records[i][this.state.webMaps.metaTitle].substr(0, 150)
            : "",
          parentZUID: this.state.records[i][this.state.webMaps.parentZUID],
          pathPart,
          sitemapPriority: this.state.webMaps.sitemapPriority,
        },
        meta: {
          contentModelZUID: this.props.modelZUID,
          createdByUserZUID: this.props.userZUID,
        },
      };
    });

    this.setState({ mappedMetaItems, inFlight: true }, () => {
      // we make the fetch call after state has been set
      this.createAllItems().then(() => {
        this.setState({ inFlight: false, complete: true });
      });
    });
  };

  createBatchItems(batch, index) {
    return Promise.allSettled(
      batch.map((item, i) => {
        return request(
          `${CONFIG.API_INSTANCE}/content/models/${this.props.modelZUID}/items`,
          {
            method: "POST",
            json: true,
            body: item,
          }
        )
          .then((res) => {
            if (res.status === 400) {
              this.props.dispatch(
                notify({
                  message: this.props.t("content.csvImportFailureCreating", {
                    error: res.error,
                  }),
                  kind: "error",
                })
              );
              this.addFailure(index * this.chunkSize + i);
            } else if (res.error) {
              this.addFailure(index * this.chunkSize + i);
            } else {
              const successes = this.state.successes + 1;
              this.setState({ successes });
              return res;
            }
          })
          .catch((err) => {
            this.addFailure(index * this.chunkSize + i);
          });
      })
    );
  }

  createAllItems() {
    const chunkedItems = chunk(this.state.mappedMetaItems, this.chunkSize);

    return chunkedItems.reduce((promise, batch, index) => {
      return promise.then((results) => {
        return this.createBatchItems(batch, index).then((data) => {
          results.push(data);
          return results;
        });
      });
    }, Promise.resolve([]));
  }

  render() {
    const { t } = this.props;
    // if import is complete, records shown are errors only
    const records = this.state.complete
      ? this.state.records.filter((record, index) =>
          this.state.failure.includes(index)
        )
      : this.state.records;

    const recordsWithColumns = [
      {
        fields: this.state.fields,
        cols: this.state.cols,
        handleMap: this.handleFieldToCSVMap,
        fieldMaps: this.state.fieldMaps,
      },
      ...records,
    ];

    return (
      <main className={styles.CSVImport}>
        <div className={styles.Top}>
          <form className={styles.File}>
            <input
              type="file"
              id="csv"
              name="csv"
              accept=".csv"
              onChange={this.handleFile}
            />
            <label htmlFor="csv">
              <FileUploadIcon fontSize="small" />
              {t("content.csvImportOpenFile")}
            </label>
          </form>
          <Button
            variant="contained"
            color="success"
            disabled={
              this.state.complete ||
              this.state.inFlight ||
              !this.state.fieldMaps ||
              !this.state.mappedItems?.length
            }
            onClick={this.handleCreateItems}
            startIcon={<AddIcon />}
          >
            {t("content.csvImportCreateItems")}
          </Button>

          {this.state.warn && (
            <Notice className={styles.Notice}>{this.state.warn}</Notice>
          )}

          <div className={cx(styles.ImportResults, styles.subheadline)}>
            {this.state.mappedMetaItems &&
              t("content.csvImportImportedCount", {
                count: this.state.successes || 0,
                total: this.state.mappedMetaItems.length,
              })}

            {this.state.failure.length ? (
              <div>
                {t("content.csvImportErrorsCount", {
                  count: this.state.failure.length,
                })}
              </div>
            ) : null}
          </div>
        </div>

        <div className={styles.Mid}>
          {/* WebData/MetaData Mapping */}
          {this.state.cols && (
            <CsvSettings
              handleMap={this.handleWebToCSVMap}
              webMaps={this.state.webMaps}
              cols={this.state.cols}
            />
          )}
        </div>
        <Box
          sx={{ height: this.state.height, width: "100%", overflowY: "scroll" }}
        >
          <List
            className={styles.Bottom}
            rowCount={recordsWithColumns.length}
            rowProps={{
              data: {
                data: recordsWithColumns,
                // if import is complete, records shown are errors only
                error: this.state.complete,
              },
            }}
            rowHeight={(index) => (index === 0 ? 71 : 61)}
            rowComponent={Row}
          />
        </Box>
      </main>
    );
  }
}

class Row extends PureComponent {
  render() {
    const item = this.props.data.data[this.props.index];
    if (this.props.index === 0) {
      return (
        <Columns
          style={this.props.style}
          handleMap={item.handleMap}
          fields={item.fields}
          cols={item.cols}
          fieldMaps={item.fieldMaps}
        />
      );
    }
    return (
      <span
        style={{ ...this.props.style, width: "auto" }}
        className={cx(styles.wrap, { [styles.failure]: this.props.data.error })}
      >
        {Object.keys(item).map((key) => {
          return (
            <div key={key} className={styles.column}>
              <span key={item[key]} className={styles.Cell}>
                {item[key].substr(0, 120)}
              </span>
            </div>
          );
        })}
      </span>
    );
  }
}

export default connect((state, props) => {
  const { modelZUID } = props.match.params;
  const model = state.models[modelZUID];
  const fields = Object.keys(state.fields)
    .filter(
      (fieldZUID) => state.fields[fieldZUID].contentModelZUID === modelZUID
    )
    .map((fieldZUID) => state.fields[fieldZUID])
    .sort((a, b) => a.sort - b.sort);
  return {
    modelZUID,
    model,
    fields,
    userZUID: state.user.user_zuid,
  };
})(withTranslation()(CSVImportBody));
